import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, ChangeDetectorRef, Optional, Output} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {RouteService} from '../../../../services/route/route.service';
import {SnackService} from '../../../../services/snack/snack.service';
import {AdminService} from '../../../../services/admin/admin.service';
import {TranslateService} from '@ngx-translate/core';
import {LanguageService} from '../../../../services/language/language.service';
import {cloneDeep, isEqual} from 'lodash-es';
import {Translation, TranslationPost, TranslationPut} from '../../../../generated/translation-client';
import {AuthService} from '../../../../services/auth/auth.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {TranslationsDialogData} from '../../translations/view/translations-view.component';
import {CONTINUE_WITHOUT_SAVING} from '../../../sales-terminal/sales-terminal.constants';
import {Subscription} from 'rxjs';
import {PendingChangesDialogComponent} from '../../../../components/pending-changes-dialog/pending-changes-dialog.component';
import {finalize} from 'rxjs/operators';
import {AngularEditorConfig} from '@kolkov/angular-editor';

@Component({
  selector: 'app-view',
  templateUrl: './templates-view.component.html',
  styleUrls: ['./templates-view.component.scss']
})
export class TemplatesViewComponent implements OnInit, OnDestroy {
  @Input() key: string;
  @Output() confirmChange: EventEmitter<any> = new EventEmitter<any>();
  private MAX_TEMPLATE_VALUE_CHAR_LENGTH: number = 32000;
  public editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: false,
    toolbarPosition: 'bottom',
    sanitize: false,
    toolbarHiddenButtons: [
      [
        'fontSize',
        'customClasses',
        'insertImage',
        'insertVideo'
      ]
    ]
  };
  public translation: Translation = null;
  public translationOrig: Translation = null;
  public selectedLanguage: string = 'en';
  public sortedLanguages: string[] = [];
  public previewMode: boolean;
  public isLoading: boolean;
  public html: SafeHtml;
  private _subs: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<TemplatesViewComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: TranslationsDialogData,
    private dialog: MatDialog,
    public routeService: RouteService,
    public languageService: LanguageService,
    private snackService: SnackService,
    private adminService: AdminService,
    private translateService: TranslateService,
    private authService: AuthService,
    private changeRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  public ngOnInit(): void {
    this.translation = { translations: {} } as Translation;
    setTimeout(() => this.syncModels(), 0);
    this.languageService.getLanguages().subscribe((languages) =>
      this.sortedLanguages = this.languageService.sortLanguages(languages, this.authService.salesPointLanguage));
    if (!this.isCreate) {
      this.getTranslation(this.routeService.params['key']);
    }
    this.dialogRef.disableClose = true;
    this._subs.push(this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    }));
  }

  public ngOnDestroy(): void {
    this.routeService.replaceUrl('/app/admin/templates');
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  syncModels(): void {
    this.translationOrig = cloneDeep(this.translation);
  }

  get isModelChanged(): boolean {
    return !isEqual(this.translation, this.translationOrig);
  }

  public sanitizedHtml(language: any): void {
    this.html = this.sanitizer.bypassSecurityTrustHtml(this.translation.translations[language]);
  }

  public get isCreate(): boolean {
    return this.routeService.checkRoute(['admin', 'templates', 'create']);
  }

  public get isEdit(): boolean {
    return this.routeService.checkRoute(['admin', 'templates', 'edit']);
  }

  public get isView(): boolean {
    return this.routeService.checkRoute(['admin', 'templates', 'view']);
  }

  public get isValid(): boolean {
    if (!this.translation?.translations) {return false;}
    const key = this.translation.key?.trim();
    return key?.length && !key.includes(' ') && !!this.translation?.translations['en'] && !this.isMaxCharLimitExceeded;
  }

  public getTranslation(key: string): void {
    this.isLoading = true;
    this.adminService.getTranslation(key, true).pipe(
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe((translation) => {
      this.translation = translation;
      this.sanitizedHtml(this.selectedLanguage);
      this.syncModels();
    });
  }

  public create(): void {
    this.adminService.createTranslation(this.getPostData()).subscribe((translation) => {
      this.translation = translation;
      this.snackService.showSuccess(this.translateService.instant('general.api_templates_successfully_created'));
      this.finishChange();
    });
  }

  public update(): void {
    this.adminService.updateTranslation(this.translation.key, this.getPutData()).subscribe((translation) => {
      this.translation = translation;
      this.snackService.showSuccess(this.translateService.instant('general.api_templates_successfully_updated'));
      this.finishChange();
    });
  }

  public preview(): void {
    setTimeout(() => {
      this.sanitizedHtml(this.selectedLanguage);
      this.previewMode = true;
      this.changeRef.detectChanges();
    }, 100);
  }

  public editMode(): void {
    this.previewMode = false;
  }

  public edit(): void {
    this.routeService.replaceUrl('/app/admin/templates/edit/' + this.translation.key);
  }

  private getPostData(): TranslationPost {
    const data = cloneDeep(this.translation) as TranslationPost;
    data.isCentreBased = true;
    this.fixFreemarkerTags(data);
    return data;
  }

  private getPutData(): TranslationPut {
    const data = cloneDeep(this.translation) as TranslationPut;
    data.isCentreBased = true;
    this.fixFreemarkerTags(data);
    return data;
  }

  private finishChange(): void {
    this.syncModels();
    this.confirmChange.emit(this.translation);
    this.close();
  }

  public get isMaxCharLimitExceeded(): boolean {
    return this.translation?.translations[this.selectedLanguage]?.length >= this.MAX_TEMPLATE_VALUE_CHAR_LENGTH
  }

  public confirmClose(): void {
    if (!this.isModelChanged) {
      this.close();
    } else {
      const pendingChanges = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_templates_description_unsaved_changes'),
        canNotSave: !this.isValid,
        buttonOk: this.isCreate ? this.translateService.instant('general.api_templates_form_create') : undefined
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          if (this.isCreate) {
            this.create();
          } else {
            this.update();
          }
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    }
  }

  private fixFreemarkerTags(data: TranslationPost | TranslationPut): void {
    const tagRegex: RegExp = /&lt;[#@]?[^&<>]*&gt;/g;
    const tagCloseRegex: RegExp = /<!--[#@]?[^&<>]*-->/g;
    Object.keys(data.translations).forEach(key => {
      if (data.translations[key]) {
        const freemarkerTagList: Array<string> = data.translations[key].match(tagRegex) || [];
        freemarkerTagList.forEach((match: string) => {
          data.translations[key] = data.translations[key].replace(match, match.replace('&lt;', '<').replace('&gt;', '>'));
        });
        const freemarkerTagCloseList: Array<string> = data.translations[key].match(tagCloseRegex) || [];
        freemarkerTagCloseList.forEach((match: string) => {
          data.translations[key] = data.translations[key].replace(match, match.replace('<!--', '</').replace('-->', '>'));
        });
      }
    });
  }

  private close(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
  }
}
